import React from "react";
import { Box, Typography, Divider, Paper, IconButton, Grid, Stack, Chip } from "@mui/material";

import PersonIcon from "@mui/icons-material/Person";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import { INDIAN_RUPEE_SIGN } from "../../constant/constant";


const ReviewStep = ({ tenantDetails, buildingOptions, propertyOptions, gstPercentage }) => {

  // Helper labels resolution for structured dynamic lookups
  const getSelectedLabel = (options, value) => {
    const match = options.find((opt) => opt.value === value?.toString());
    return match ? match.label : value || "—";
  };

  // Helper row visual component for preview cards
  const PreviewRow = ({ label, value, highlight = false }) => (
    <Box sx={{ display: "flex", justifyContent: "space-between", py: 1, borderBottom: "1px dashed #F2F4F7" }}>
      <Typography variant="body2" sx={{ color: "#667085", fontWeight: "500" }}>{label}</Typography>
      <Typography variant="body2" sx={{ color: highlight ? "#101828" : "#344054", fontWeight: highlight ? "700" : "600" }}>
        {value || "—"}
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ mt: 1 }}>
      <Grid container spacing={3}>

        {/* CARD 1: Tenant Information */}
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: "10px", borderColor: "#EAECF0", height: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
              <PersonIcon sx={{ color: "#1877F2" }} />
              <Typography variant="subtitle1" sx={{ fontWeight: "700", color: "#101828" }}>Tenant Information</Typography>
            </Box>
            <Stack spacing={0.5}>
              <PreviewRow label="Full Name" value={`${tenantDetails.tenant_first_name} ${tenantDetails.tenant_last_name}`} />
              <PreviewRow label="Phone Number" value={tenantDetails.tenant_contact} />
              <PreviewRow label="Tenant Type" value={tenantDetails.tenant_type == 1 ? "Pre-paid" : "Post-paid"} />
            </Stack>
          </Paper>
        </Grid>

        {/* CARD 2: Rental Location Details */}
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: "10px", borderColor: "#EAECF0", height: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
              <LocationOnIcon sx={{ color: "#1877F2" }} />
              <Typography variant="subtitle1" sx={{ fontWeight: "700", color: "#101828" }}>Lease Location & Term</Typography>
            </Box>
            <Stack spacing={0.5}>
              <PreviewRow label="Building" value={getSelectedLabel(buildingOptions, tenantDetails.building_id)} />
              <PreviewRow label="Property" value={getSelectedLabel(propertyOptions, tenantDetails.property_id)} />
              <PreviewRow label="Move-In Date" value={new Date(tenantDetails.entry_date).toLocaleDateString("en-GB")} />
              <PreviewRow label="Move-Out Date" value={new Date(tenantDetails.exit_date).toLocaleDateString("en-GB") || "Open Ended"} />
            </Stack>
          </Paper>
        </Grid>

        {/* CARD 3: Complete Rent Configuration */}
        <Grid item xs={12} md={7}>
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: "10px", borderColor: "#EAECF0", height: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
              <AccountBalanceWalletIcon sx={{ color: "#1877F2" }} />
              <Typography variant="subtitle1" sx={{ fontWeight: "700", color: "#101828" }}>Rent Configuration & Breakup</Typography>
            </Box>
            <Stack spacing={0.5}>
              <PreviewRow label="Monthly Rent" value={`${INDIAN_RUPEE_SIGN} ${tenantDetails.rent_amount || "0.00"}`} />
              <PreviewRow label="Maintenance Fee" value={`${INDIAN_RUPEE_SIGN} ${tenantDetails.maintenance_charges || "0.00"}`} />
              <PreviewRow label="Electricity Rate" value={`${INDIAN_RUPEE_SIGN} ${tenantDetails.electricity_per_unit_charge || "0.00"}`} />
              <PreviewRow label="Electricity Charge" value={`${INDIAN_RUPEE_SIGN} ${tenantDetails.monthly_electricity_bill || "0.00"}`} />
              <PreviewRow label="GST Liability Calculated" value={`${INDIAN_RUPEE_SIGN} ${tenantDetails.gst_amount || "0.00"}`} />
              {tenantDetails.apply_tds && (
                <PreviewRow label="TDS Deduction" value={`${INDIAN_RUPEE_SIGN} -${tenantDetails.tds_deduction || "0.00"}`} />
              )}
              <PreviewRow label="Adjustment" value={tenantDetails.round_off || "0.00"} />

              <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2, pt: 1.5, borderTop: "2px solid #EAECF0" }}>
                <Typography variant="subtitle1" sx={{ color: "#101828", fontWeight: "700" }}>Net Invoiced Rent</Typography>
                <Typography variant="h6" sx={{ color: "#2E7D32", fontWeight: "800" }}>
                  {`${INDIAN_RUPEE_SIGN} ${tenantDetails.total_rent_amount || "0.00"}`}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>

        {/* CARD 4: Compliance & Settings */}
        <Grid item xs={12} md={5}>
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: "10px", borderColor: "#EAECF0", height: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
              <ReceiptLongIcon sx={{ color: "#1877F2" }} />
              <Typography variant="subtitle1" sx={{ fontWeight: "700", color: "#101828" }}>Tax & Compliance</Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
              <Chip
                label={tenantDetails.apply_gst ? `GST Enabled (${gstPercentage}%)` : "No GST"}
                color={tenantDetails.apply_gst ? "primary" : "default"}
                size="small"
                variant="soft"
                sx={{ fontWeight: "600" }}
              />
              <Chip
                label={tenantDetails.apply_tds ? "TDS Applied" : "No TDS"}
                color={tenantDetails.apply_tds ? "secondary" : "default"}
                size="small"
                sx={{ fontWeight: "600" }}
              />
            </Box>
            <Stack spacing={0.5}>
              {tenantDetails.apply_gst && <PreviewRow label="GSTIN" value={tenantDetails.gst_number} />}
              {tenantDetails.apply_tds && <PreviewRow label="TDS Type" value={tenantDetails.tds_type} />}
              <PreviewRow label="PAN Number" value={tenantDetails.pan_number} />
              <PreviewRow label="Aadhar Number" value={tenantDetails.aadhar_number} />
              <Divider sx={{ my: 1 }} />
              <PreviewRow label="Rent Increase Schedule" value={tenantDetails.annual_increment_type ? `${tenantDetails.annual_increment_type} Months` : "None"} />
              <PreviewRow label="Next Increase Date" value={new Date(tenantDetails.annual_increment_date).toLocaleDateString("en-GB")} />
              <PreviewRow label="Increase (%)" value={tenantDetails.increment_precentage ? `${tenantDetails.increment_precentage}%` : "0%"} />
            </Stack>
          </Paper>
        </Grid>

        {/* Additional Comments Full-Width Section */}
        {tenantDetails.notes && (
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: "10px", borderColor: "#EAECF0", backgroundColor: "#F8F9FA" }}>
              <Typography variant="subtitle2" sx={{ fontWeight: "700", color: "#475467", mb: 0.5 }}>Administrative Lease Notes</Typography>
              <Typography variant="body2" sx={{ color: "#344054", fontStyle: "italic" }}>{tenantDetails.notes}</Typography>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default ReviewStep;